import type { ConsentAudience } from '../../lib/consents';

const LINK_CLASS = 'text-brand-carnaby-pink underline';
const TERMS_URL = 'https://www.reed.ai/terms-and-conditions';
const PRIVACY_URL = 'https://www.reed.ai/privacy-policy';

export interface OnboardingTermsSummaryProps {
  audience: ConsentAudience;
}

// Toned-down terms summary shown in the onboarding acceptance step
// (TermsAcceptanceStep). This is a shorter onboarding-only version that links
// out to the full Reed.ai legal text.
//
// Candidate and employer copy diverge — this is deliberate and matches the
// approved Figma frames (C-3 / E-3):
//   • Candidate consent to the terms is IMPLIED on "Create account", so the
//     lead paragraph says "By clicking create account…" and there is no
//     Reed Group paragraph on the candidate frame.
//   • Employers tick a required box, so the lead paragraph says "By creating an
//     account…" and the fuller marketing + Reed Group copy is shown.
export function OnboardingTermsSummary({ audience }: OnboardingTermsSummaryProps): JSX.Element {
  const isCandidate = audience === 'candidate';

  return (
    <div className="space-y-4 text-sm leading-relaxed text-text-primary">
      <p>
        {isCandidate ? 'By clicking create account, you agree to ' : 'By creating an account you agree to '}
        Reed.ai&rsquo;s{' '}
        <a href={TERMS_URL} className={LINK_CLASS} target="_blank" rel="noopener noreferrer">
          Terms and Conditions
        </a>{' '}
        and you acknowledge that you have read Reed.ai&rsquo;s{' '}
        <a href={PRIVACY_URL} className={LINK_CLASS} target="_blank" rel="noopener noreferrer">
          Privacy Notice
        </a>{' '}
        which will apply to the processing of your personal data in the provision of our services.
      </p>

      {isCandidate ? (
        <p>Please check the box if you do not want to receive marketing communications from Reed.ai.</p>
      ) : (
        <>
          <p>
            From time to time, Reed.ai will provide you with marketing communications in relation to
            products and services which may be of interest to you. You can unsubscribe from Reed.ai
            communications at any time by clicking the relevant link in the marketing emails. If you
            do not want to receive these, please tick the box below.
          </p>
          <p>
            Whilst Reed.ai is part of the Reed Group, marketing consents for other Reed products are
            managed separately, hence you may still receive marketing communications from Reed in
            relation to other goods and services you may have subscribed to.
          </p>
        </>
      )}
    </div>
  );
}